import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    ITask,
    ITaskAssignmentErrorResponse,
    ITaskAssignmentRequest,
    IUserLoginInfo,
    ODataResponse,
} from '@models';
import { ODataQueryOptions } from '@services/generics/odata.query';
import { ODataService } from '@services/generics/odata.service';
import { MapperService } from '@services/mapper.service';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class TaskService extends ODataService<ITask, number> {
    protected _endpoint: string;

    constructor(http: HttpClient) {
        super(http, MapperService.taskMapper);
        this._endpoint = '/odata/Tasks';
    }

    public assignToUser(tasks: ITask[], userId: number): Observable<ODataResponse<ITaskAssignmentErrorResponse>> {
        const url = `${this._endpoint}/UiPath.Server.Configuration.OData.AssignTasks`;
        return this._http.post(url, this._createTaskAssignmentPayload(tasks, userId))
            .pipe(
                map(res => this._mapData<ITaskAssignmentErrorResponse>(res, MapperService.taskAssignmentResponseMap)),
            ) as Observable<ODataResponse<ITaskAssignmentErrorResponse>>;
    }

    public reAssignToUser(tasks: ITask[], userId: number): Observable<ODataResponse<ITaskAssignmentErrorResponse>> {
        const url = `${this._endpoint}/UiPath.Server.Configuration.OData.ReassignTasks`;
        return this._http.post(url, this._createTaskAssignmentPayload(tasks, userId))
            .pipe(
                map(res => this._mapData<ITaskAssignmentErrorResponse>(res, MapperService.taskAssignmentResponseMap)),
            ) as Observable<ODataResponse<ITaskAssignmentErrorResponse>>;
    }

    public unAssignFromUser(tasks: ITask[]): Observable<ODataResponse<ITaskAssignmentErrorResponse>> {
        const url = `${this._endpoint}/UiPath.Server.Configuration.OData.UnassignTasks`;
        return this._http.post(url, this._createTaskUnassignmentPayload(tasks))
            .pipe(
                map(res => this._mapData<ITaskAssignmentErrorResponse>(res, MapperService.taskAssignmentResponseMap)),
            ) as Observable<ODataResponse<ITaskAssignmentErrorResponse>>;
    }

    public suggestTaskUsers(organizationUnitId: number, searchTerm: string = '', fetchSize = 20, skip: number = 0) {
        const queryOptions: ODataQueryOptions = {
            select: ['UserName', 'Id'],
            orderBy: ['UserName'],
            top: fetchSize,
            skip,
            filter: {
                UserName: {
                    startswith: searchTerm,
                },
            },
        };

        const endpoint = `${this._endpoint}/UiPath.Server.Configuration.OData.GetTaskUsers(organizationUnitId=${organizationUnitId})`;

        return this._http
            .get(`${endpoint}${this.getQueryString(queryOptions)}`)
            .pipe(
                map(response => this._mapData(response, MapperService.userLoginInfoMap) as ODataResponse<IUserLoginInfo>),
                map(
                    ({ data, total }) =>
                        ({
                            total,
                            data: data.map(user => ({
                                id: user.id,
                                text: user.userName,
                            })),
                        }),
                ),
            );
    }

    private _createTaskAssignmentPayload(tasks: ITask[], userId?: number): string {
        const taskAssignments = tasks.map(task => this._toPoco(<ITaskAssignmentRequest>{
            userId: userId,
            taskId: task.id,
        }, MapperService.taskAssignmentRequestMap));
        return JSON.stringify({ taskAssignments });
    }

    private _createTaskUnassignmentPayload(tasks: ITask[]): string {
        const taskIds = tasks.map(task => task.id);
        return JSON.stringify({ taskIds });
    }
}
